// The three online builders, shared by the header search panel/sheet and the search results page.
// Kept free of React state so server components (app/search) can import it.
type GlyphProps = React.SVGProps<SVGSVGElement>;

// Env-overridable like the PDP flow (product-details/helpers/railingBuilder.ts), so a staging site can
// point every builder link at a staging builder.
const RAILING_BUILDER_URL = process.env.NEXT_PUBLIC_RAILING_BUILDER_URL || 'https://design.viewrail.com/';
const TREAD_BUILDER_URL = process.env.NEXT_PUBLIC_TREAD_BUILDER_BASE_URL || 'https://treads.viewrail.com/build';
const GLASS_BUILDER_URL = process.env.NEXT_PUBLIC_GLASS_BUILDER_URL || 'https://glass.viewrail.com/';

// Product glyphs for the builders. Heroicons has nothing for railing, treads or glass (the stand-ins read
// as "columns", "menu" and "browser window"), so these are drawn on the same 24px grid and 1.5 stroke to
// sit beside the Heroicons arrow without looking foreign.
const glyph = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

// A cable railing section: a heavy top rail on two posts, with hairline cable runs between. The strong
// rail-vs-cable weight contrast keeps it reading as railing rather than a table, ladder or grid at 18px.
const RailingGlyph = (props: GlyphProps) => (
  <svg {...glyph} {...props}>
    <path d="M3.5 5.5h17" strokeWidth={2.5} />
    <path d="M5 5.5V20M19 5.5V20" />
    <path d="M5 10h14M5 13.5h14M5 17h14" strokeWidth={0.75} opacity={0.85} />
  </svg>
);

// Two steps rising to a landing; each tread runs past its riser, like a nosing overhang.
const TreadGlyph = (props: GlyphProps) => (
  <svg {...glyph} {...props}>
    <path d="M3 20h6M9 20v-6M7 14h8M15 14V8M13 8h8" />
  </svg>
);

// A run of frameless glass railing: two panes side by side, each with a thin glint.
const GlassGlyph = (props: GlyphProps) => (
  <svg {...glyph} {...props}>
    <rect x="3" y="5" width="8" height="14" />
    <rect x="13" y="5" width="8" height="14" />
    <path d="M5.5 11l3-3M15.5 11l3-3" strokeWidth={1} />
  </svg>
);

// `keywords` pick the builder to suggest on /search: a query containing any of them (as a word prefix)
// surfaces that builder above the results.
export const BUILDER_TOOLS = [
  {
    label: 'Railing Builder',
    tagline: 'Lay out runs, get a priced kit',
    href: RAILING_BUILDER_URL,
    Icon: RailingGlyph,
    keywords: ['railing', 'rail', 'cable', 'rod', 'post', 'drivetite', 'express', 'signature', 'deck'],
  },
  {
    label: 'Tread Builder',
    tagline: 'Enter sizes, get a cut list',
    href: TREAD_BUILDER_URL,
    Icon: TreadGlyph,
    keywords: ['tread', 'step', 'landing', 'riser', 'oak', 'maple', 'walnut', 'hickory'],
  },
  {
    label: 'Glass Panel Builder',
    tagline: 'Size custom glass panels',
    href: GLASS_BUILDER_URL,
    Icon: GlassGlyph,
    keywords: ['glass', 'panel', 'talon', 'standoff', 'frameless', 'starphire'],
  },
];

// Most specific first, so "glass railing" suggests the glass builder and "stair railing" the railing
// one. ("stair" is deliberately not a tread keyword: it appears in railing and FLIGHT searches too.)
const MATCH_PRIORITY = ['Glass Panel Builder', 'Railing Builder', 'Tread Builder'];

// The builder to suggest for a search query, or undefined when none fits.
export const builderForQuery = (query: string) => {
  const words = query.toLowerCase().split(/[^a-z0-9]+/);
  return MATCH_PRIORITY.map((label) => BUILDER_TOOLS.find((tool) => tool.label === label)).find(
    (tool) => tool && tool.keywords.some((keyword) => words.some((word) => word.startsWith(keyword))),
  );
};

// The header subnav's builder link per shop section, with its "Build Custom …" label. Chosen by the
// section alone, never the subcategory: Handrail's "Glass Top Cap" listing must not pick up the glass
// builder. Sections without an entry (Handrail, FLIGHT Stairs) show no builder link.
const SECTION_BUILDERS: Record<string, { tool: string; label: string }> = {
  'cable railing': { tool: 'Railing Builder', label: 'Build Custom Railing' },
  'glass railing': { tool: 'Glass Panel Builder', label: 'Build Custom Glass Panels' },
  'stair treads': { tool: 'Tread Builder', label: 'Build Custom Treads' },
};

export const builderForSection = (sectionName?: string) => {
  const entry = SECTION_BUILDERS[sectionName?.trim().toLowerCase() ?? ''];
  const tool = entry && BUILDER_TOOLS.find((builder) => builder.label === entry.tool);
  return tool && { label: entry.label, href: tool.href, Icon: tool.Icon };
};
